// ABOUTME: Client-side chart data validation utilities.
// ABOUTME: Provides type-safe validation matching backend rules for single and multi-series data.

export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'stacked-bar' | 'grouped-bar';

export interface ValidationError {
  field: string;
  message: string;
  row_index?: number;
  column?: string;
}

export interface ChartStructure {
  categoryKey: string | null;
  seriesKeys: string[];
}

// Priority order for detecting which key is the category (label) axis.
// If a key name matches one of these, it takes priority over other string keys.
const CATEGORY_KEY_PRIORITY = ['label', 'name', 'category', 'date', 'month', 'year', 'quarter'];

/**
 * Detects the chart structure from an array of row objects.
 * Identifies which key is the category axis and which keys are numeric series.
 * Uses the first row to determine types: string-valued keys are candidates for
 * the category axis, number-valued keys are series.
 */
export function detectChartStructure(data: Record<string, unknown>[]): ChartStructure {
  if (data.length === 0) {
    return { categoryKey: null, seriesKeys: [] };
  }

  const firstRow = data[0];
  const keys = Object.keys(firstRow);

  const stringKeys: string[] = [];
  const numericKeys: string[] = [];

  for (const key of keys) {
    const val = firstRow[key];
    if (typeof val === 'string') {
      stringKeys.push(key);
    } else if (typeof val === 'number') {
      numericKeys.push(key);
    }
  }

  // Determine category key using priority list, falling back to the first string key
  let categoryKey: string | null = null;
  for (const preferred of CATEGORY_KEY_PRIORITY) {
    if (stringKeys.includes(preferred)) {
      categoryKey = preferred;
      break;
    }
  }
  if (categoryKey === null && stringKeys.length > 0) {
    categoryKey = stringKeys[0];
  }

  return {
    categoryKey,
    seriesKeys: numericKeys,
  };
}

/**
 * Validates chart data (single or multi-series) against backend rules.
 * Accepts an array of row objects with arbitrary keys. The structure is
 * auto-detected: one string key becomes the category axis, all numeric
 * keys become series.
 */
export function validateChartData(
  data: Record<string, unknown>[],
  chartType: ChartType
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Minimum data points
  if (data.length < 2) {
    errors.push({
      field: 'data',
      message: 'Charts require at least 2 data points',
    });
    return errors;
  }

  // Maximum data points
  if (data.length > 1000) {
    errors.push({
      field: 'data',
      message: 'Charts support maximum 1000 data points',
    });
    return errors;
  }

  // Check key consistency: all rows should have the same set of keys as the first row
  const expectedKeys = Object.keys(data[0]).sort().join(',');
  for (let i = 1; i < data.length; i++) {
    const rowKeys = Object.keys(data[i]).sort().join(',');
    if (rowKeys !== expectedKeys) {
      errors.push({
        field: 'data',
        message: `Inconsistent keys at row ${i}: expected [${expectedKeys}] but got [${rowKeys}]`,
        row_index: i,
      });
      return errors;
    }
  }

  // Detect structure from the data
  const structure = detectChartStructure(data);
  const { categoryKey, seriesKeys } = structure;

  // Pie charts cannot have multiple series
  if (chartType === 'pie' && seriesKeys.length > 1) {
    errors.push({
      field: 'data',
      message: 'Pie charts do not support multiple series; use a single value column',
    });
    return errors;
  }

  // Validate each row
  data.forEach((row, index) => {
    // Validate category value if a category key was detected
    if (categoryKey !== null) {
      const catVal = row[categoryKey];
      if (typeof catVal !== 'string' || catVal.trim() === '') {
        errors.push({
          field: `data[${index}].${categoryKey}`,
          message: `Category value is required and must be a non-empty string`,
          row_index: index,
          column: categoryKey,
        });
      }
    }

    // Validate each series value
    for (const seriesKey of seriesKeys) {
      const val = row[seriesKey];

      if (val === undefined || val === null) {
        errors.push({
          field: `data[${index}].${seriesKey}`,
          message: `Value is required for "${seriesKey}"`,
          row_index: index,
          column: seriesKey,
        });
        continue;
      }

      if (typeof val !== 'number') {
        errors.push({
          field: `data[${index}].${seriesKey}`,
          message: `Value for "${seriesKey}" must be a number`,
          row_index: index,
          column: seriesKey,
        });
        continue;
      }

      if (!isFinite(val)) {
        errors.push({
          field: `data[${index}].${seriesKey}`,
          message: `Value for "${seriesKey}" must be finite (no NaN or Infinity)`,
          row_index: index,
          column: seriesKey,
        });
        continue;
      }

      // Pie chart: all values must be positive
      if (chartType === 'pie' && val <= 0) {
        errors.push({
          field: `data[${index}].${seriesKey}`,
          message: `Pie chart values must be positive (got ${val} for "${seriesKey}")`,
          row_index: index,
          column: seriesKey,
        });
      }
    }
  });

  return errors;
}
